import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'
import { BrowserRouter, useLocation } from 'react-router-dom'
import type { ReactNode } from 'react'
import App from './App'
import { AuthProvider, routePermission, useAuth } from './auth'
import { ProductDataProvider, type ProductDataResource } from './data'
import './styles.css'

function RoutedProductDataProvider({ children }: { children: ReactNode }) {
  const { pathname } = useLocation()
  const auth = useAuth()
  const permission = routePermission(pathname)
  const isCompanyProducts = pathname.startsWith('/company/products')
  const isIndependentScope = (pathname.startsWith('/company') && !isCompanyProducts)
    || pathname.startsWith('/pinduoduo')
    || pathname.startsWith('/douyin')
    || pathname.startsWith('/jd/profit-calculator')
    || pathname.startsWith('/jd/costs')
    || pathname.startsWith('/jd/orders')
    || pathname.startsWith('/jd/warehouse-slow-moving')
    || pathname.startsWith('/realtime')
    || pathname.startsWith('/advertising/fee-ratio')
    || pathname.startsWith('/advertising/spend-comparison')
    || pathname.startsWith('/admin')
    || pathname.startsWith('/forbidden')
    || pathname.startsWith('/login')
    || pathname.startsWith('/change-password')
    || pathname.startsWith('/shared-calculations/')
  const authorized = !permission || auth.canAccess(permission[0], permission[1])
  const enabled = !auth.loading && Boolean(auth.user) && !auth.user?.mustChangePassword && authorized && !isIndependentScope
  const resources: ProductDataResource[] = pathname === '/'
    ? ['data', 'segments', 'master', 'internal', 'advertising']
    : pathname.startsWith('/company/products')
      ? ['data']
    : pathname.startsWith('/advertising/orders')
      ? ['data', 'internal', 'adOrders']
      : pathname.startsWith('/profit') || pathname.startsWith('/advertising')
        ? ['data', 'master', 'internal', 'advertising']
      : pathname.startsWith('/analysis')
        ? ['data']
        : pathname.startsWith('/spu') || pathname.startsWith('/product/spu/')
          ? ['data', 'segments', 'master', 'internal', 'notes', 'images', 'erp']
          : pathname.startsWith('/sku') || pathname.startsWith('/product/sku/')
            ? ['data', 'segments', 'master', 'internal', 'images', 'erp']
            : pathname.startsWith('/product-master')
              ? ['data', 'segments', 'master', 'internal', 'images', 'erp']
              : pathname.startsWith('/new-products') || pathname.startsWith('/campaign-analysis')
                ? ['data', 'segments', 'master', 'internal']
                : pathname.startsWith('/inventory')
                  ? ['data', 'erp']
                  : pathname.startsWith('/segments')
                    ? ['data', 'segments']
                    : pathname.startsWith('/data')
                      ? ['data', 'internal']
                      : ['data']
  return <ProductDataProvider key={auth.user?.id || 'guest'} enabled={enabled} resources={resources}>{children}</ProductDataProvider>
}

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <BrowserRouter>
      <AuthProvider>
        <RoutedProductDataProvider><App /></RoutedProductDataProvider>
      </AuthProvider>
    </BrowserRouter>
  </StrictMode>,
)
